
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, NodeType, IconType } from '../types';
import { appReducer, AppAction } from '../store/reducer';
import { MAX_PATIENCE_TICKS, DEFAULT_PIXELS_PER_METER } from '../constants';

export const initialState: AppState = {
  nodes: [],
  agents: [],
  walls: [],
  zones: [],
  paths: [],
  icons: [],
  floorPlanImage: null,
  mapOpacity: 1,
  mapBrightness: 1,
  mapContrast: 1.1,
  isRunning: false,
  editMode: 'SELECT',
  viewMode: 'NORMAL',
  selectedNodeType: NodeType.HALLWAY,
  selectedIconType: IconType.DOCTOR,
  selectedItems: [],
  selectedPathPoint: null,
  selectedAgentId: null,
  simulationSpeed: 1,
  patientPatience: MAX_PATIENCE_TICKS,
  pixelsPerMeter: DEFAULT_PIXELS_PER_METER,
  calibrationPoints: [],
  measurementPoints: [],
  stats: {
    treatedPatients: 0,
    activePatients: 0,
    avgWaitTime: 0,
    avgDistanceTraveled: 0,
    avgDistanceByRole: { patients: 0, doctors: 0, nurses: 0, assistants: 0 },
    staffUtilization: { doctors: 0, nurses: 0, assistants: 0 },
    criticalEvents: 0,
    infections: 0,
    lwbs: 0,
    avgDoorToDoc: 0,
    avgLengthOfStay: 0,
    avgLabTurnaround: 0,
    bedOccupancyRate: 0
  },
  isAutoSpawning: false,
  autoSpawnRate: 20, 
  simConfig: {
      labProcessingTime: 300, 
      admissionRate: 15, 
      dischargeProcessTime: 100,
      imagingRate: 50,
      basePatientSpeed: 1.2,
      baseStaffSpeed: 1.8,
      patienceDepletionRate: 1,
      infectionProbability: 0.05,
      infectionRadius: 30,
      nodeContaminationRate: 0.5,
      staffPrioritization: 'ACUITY',
      avoidContamination: false,
      allowHallwayWaiting: true,
      congestionAwareness: 0.3,
      skipTriageForCritical: true,
      strictIsolationProtocol: false,
      diagnosticsFirst: false,
      fastTrackTrauma: true
  },
  layoutVersion: 0,
  history: [],
  future: [],
  isHelpOpen: false,
  // Fix: Added missing initial state for report modal
  isReportOpen: false,
  helpTab: 'BASICS',
  isBehaviorModalOpen: false,
  isControlsVisible: true,
};

interface AppContextProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};
